/**
 * Blocking PIN creation flow: Create → Confirm → API.
 * Shown when device_access_mode is 'pin' and the user has no PIN (e.g. first login after admin enabled).
 */

import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import LockScreen from "./LockScreen";
import PinEntry from "./PinEntry";
import { createPin } from "@common/lib/api/pins";

export interface PinCreationScreenProps {
    clinicName: string;
    clinicLocation?: string | null;
    accountId: string;
    onCreated: () => void;
    onSignInWithEmailInstead?: () => void | Promise<void>;
}

export default function PinCreationScreen({
    clinicName,
    clinicLocation,
    accountId,
    onCreated,
    onSignInWithEmailInstead,
}: PinCreationScreenProps) {
    const [step, setStep] = useState<"create" | "confirm">("create");
    const [pinToConfirm, setPinToConfirm] = useState("");
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [, setConfirmErrorCount] = useState(0);

    const handleCreateComplete = useCallback((pin: string) => {
        setPinToConfirm(pin);
        setStep("confirm");
        setError("");
    }, []);

    const handleConfirmComplete = useCallback(
        async (pin: string) => {
            if (pin !== pinToConfirm) {
                setError("PINs don't match — try again.");
                setConfirmErrorCount((prev) => prev + 1);
                return;
            }
            setError("");
            setIsSubmitting(true);
            try {
                await createPin({ pin, account_id: accountId });
                toast.success("PIN created. You can now use it to unlock this device.");
                onCreated();
            } catch (e: unknown) {
                const message =
                    (e as { response?: { status?: number }; message?: string })?.response?.status === 409 ||
                    String((e as Error).message)
                        .toLowerCase()
                        .includes("already in use")
                        ? "This PIN is already in use — pick another."
                        : "Something went wrong. Please try again.";
                setError(message);
                setStep("create");
                setPinToConfirm("");
            } finally {
                setIsSubmitting(false);
            }
        },
        [accountId, pinToConfirm, onCreated],
    );

    return (
        <LockScreen
            clinicName={clinicName}
            clinicLocation={clinicLocation}
            mode="pin"
            logo="hd-dark"
            onSignInWithEmailInstead={onSignInWithEmailInstead}
        >
            <div className="w-full max-w-sm">
                {step === "create" && (
                    <PinEntry
                        theme="dark"
                        variant="create"
                        onComplete={handleCreateComplete}
                        hasError={Boolean(error)}
                        disabled={isSubmitting}
                    />
                )}
                {step === "confirm" && (
                    <PinEntry
                        theme="dark"
                        variant="confirm"
                        onComplete={handleConfirmComplete}
                        hasError={Boolean(error)}
                        disabled={isSubmitting}
                    />
                )}
                {error && (
                    <p className="mt-4 text-center text-error-200 text-sm" role="alert">
                        {error}
                    </p>
                )}
            </div>
        </LockScreen>
    );
}
